import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import UPLOAD_INVOICE_DOCUMENTS from '../graphql/mutations/upload-invoice-documents';
import useSnackBars from './useSnackBars';

const normalizeDocumentsFromResponse = (data, invoiceId) => {
  const invoiceDocuments = data?.uploadInvoiceDocuments?.invoice?.documents;
  if (invoiceDocuments != null) return invoiceDocuments;

  const invoicesEdges = data?.uploadInvoiceDocuments?.invoices?.edges;
  if (invoicesEdges == null) return [];

  const matchedInvoice = invoicesEdges.find(
    ({ node }) => `${node?.id}` === `${invoiceId}`,
  );

  return matchedInvoice?.node?.documents || [];
};

const getUploadedDocumentUrl = (uploadedDocuments, documentType) => (
  uploadedDocuments
    ?.slice()
    .reverse()
    .find((doc) => doc?.type?.code === documentType)?.file?.url
    || null
);

const useInvoiceDocumentUpload = ({
  invoiceId,
  showSnackbar = false,
  onSuccess,
}) => {
  const inputRef = useRef();
  const [documentToUpload, setDocumentToUpload] = useState({});
  const [uploadedUrlsByType, setUploadedUrlsByType] = useState({});
  const { addAlert } = useSnackBars();

  const [uploadInvoiceDocuments, { loading }] = useMutation(UPLOAD_INVOICE_DOCUMENTS);

  const handleOpenInput = (document) => {
    setDocumentToUpload(document || {});
    inputRef.current?.click();
  };

  const handleUpload = useCallback((file, documentType) => {
    uploadInvoiceDocuments({
      variables: {
        invoiceId,
        inputUploadFiles: [
          {
            documentType,
            file,
          },
        ],
      },
      onCompleted: (data) => {
        const uploadedDocuments = normalizeDocumentsFromResponse(data, invoiceId);
        const uploadedUrl = getUploadedDocumentUrl(uploadedDocuments, documentType);

        if (uploadedUrl) {
          setUploadedUrlsByType((prevState) => ({
            ...prevState,
            [documentType]: uploadedUrl,
          }));
        }

        if (showSnackbar) {
          addAlert({
            id: 'upload-invoice-documents-success',
            severity: 'success',
            message: 'Documento cargado con éxito',
          });
        }

        onSuccess?.();
      },
      onError: () => {
        if (!showSnackbar) return;
        addAlert({
          id: 'upload-invoice-documents-error',
          severity: 'error',
          message: 'Ha ocurrido un error. Verifique que los archivos estén correctos.',
        });
      },
    });
  }, [addAlert, invoiceId, onSuccess, showSnackbar, uploadInvoiceDocuments]);

  const handleChange = (event) => {
    const target = event?.target;
    const file = target?.files?.[0];
    const currentDocumentType = documentToUpload?.documentType;

    if (!file || !currentDocumentType) return;

    handleUpload(file, currentDocumentType);
    target.value = '';
  };

  return {
    inputRef,
    documentToUpload,
    loading,
    handleOpenInput,
    handleChange,
    handleUpload,
    getUploadedUrlByType: (documentType) => uploadedUrlsByType[documentType] || null,
  };
};

export default useInvoiceDocumentUpload;
